import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { authApi } from 'api';
import { InputCheckbox } from 'components/_new/InputCheckbox';
import { useAuth } from 'contexts/authContext';
import { translatedStaticResourceResolver } from 'utils/translatedStaticResourceResolver';
import { Box, BoxSection } from 'components/_new/Box';
import { Button } from 'components/_new/Button';
import { Logo } from 'components/_new/Logo';

interface RegisterFormObject {
	terms: boolean;
}

const RequireTerms: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { currentUser, currentUserRefetch } = useAuth();

	const { control, handleSubmit } = useForm<RegisterFormObject>({
		defaultValues: {
			terms: false,
		},
	});

	const { mutate, isLoading } = useMutation(() => authApi.acceptTerms(), {
		onSuccess: () => {
			currentUserRefetch();
			navigate('/');
		},
	});

	return (
		<>
			<Helmet title={t('auth.register')} />
			<div className="flex flex-column align-items-center justify-content-center row-gap-6 py-3 w-full px-3">
				<Box variant="white" dropShadow className="w-full" style={{ maxWidth: '480px' }}>
					<BoxSection className="px-4 sm:px-6 py-4 text-center" contentClassName="w-full">
						<Logo size="xl" className="mt-2 mb-3 align-self-center" />

						{currentUser?.terms ? (
							<div className="flex flex-column justify-content-center align-items-center">
								<p>{t('auth.termsAccepted')}</p>
								<Button
									submit
									onClick={() => {
										navigate('/');
									}}
									label={t('auth.changeProgram')}
									block
								/>
							</div>
						) : (
							<form onSubmit={handleSubmit(() => mutate())} className="flex flex-column row-gap-4">
								<p>{t('auth.termsAcceptanceRequired')}</p>
								<Controller
									name="terms"
									control={control}
									rules={{ required: true }}
									render={({ field, fieldState }) => (
										<InputCheckbox
											inputId="termsOfService"
											label={t('auth.accept')}
											invalid={Boolean(fieldState.error)}
											checked={Boolean(field.value)}
											onChange={(event) => field.onChange(Boolean(event.checked))}
											onBlur={field.onBlur}
											inputRef={field.ref}
											labelClassName="text-sm"
										>
											{t('auth.accept')}{' '}
											<a
												href={translatedStaticResourceResolver('terms')}
												target="_blank"
												className={fieldState.error ? 'text-danger' : ''}
												rel="noreferrer"
											>
												{t('auth.termsOfService')}
											</a>
										</InputCheckbox>
									)}
								/>
								<p className="text-sm text-muted-color mb-3">{t('auth.registerTooltip2')}</p>
								<Button submit loading={isLoading} label={t('auth.finishSigningProcess')} block />
							</form>
						)}
					</BoxSection>
				</Box>
			</div>
		</>
	);
};

export default RequireTerms;
